import React, { useState } from "react";
import { Box, Skeleton } from "@mui/material";
import DotLegalPaper from "../common/components/dotLegalPaper/DotLegalPaper";
import { useTranslation } from "../localization/useTranslation";
import { useLegalEntityHook } from "./LegalEntity.hooks";
import { toTopTabIndex } from "@common/hooks/useOverviewTopTab";
import { useLegalEntityStyles } from "./LegalEntity.styles";
import { useNavigate, useParams } from "react-router";
import DotLegalDeleteDialogWrapper from "../common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import LegalEntityDocumentTab from "./legalEntityDocumentTab/LegalEntityDocumentTab";
import SharingsBaseTable from "../processingActivity/components/sharingsTable/SharingsTable";
import { ChangeCustomerOwnedDialog } from "./changeCustomerOwnedDialog/ChangeCustomerOwnedDialog";
import { TogglePreselectedOnCreateDialog } from "./togglePreselectedOnCreateDialog/TogglePreselectedOnCreateDialog";
import { StandardLegalEntityType } from "./LegalEntity.types.ts";
import { useLegalEntityTypeNames } from "./useLegalEntityTypeNames.ts";
import { DotLegalChip, DotLegalFullScreenSpinner, DotLegalLink, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import { usePlanContext } from "../auth/planProvider/PlanProvider";
import { usePermissionsProvider } from "../auth/usePermissionsProvider";
import LegalEntityContactTab from "./legalEntityContactTab/LegalEntityContactTab";
import LegalEntityAuditTab from "./legalEntityAuditTab/LegalEntityAuditTab";
import LegalEntityThirdCountryDialog from "./legalEntityThirdCountryDialog/LegalEntityThirdCountryDialog";
import EntityRiskAreasDialog from "../risk/entityRiskAreasDialog/EntityRiskAreasDialog";
import { LegalEntityScope } from "./legalEntities/LegalEntities.types";
import { useUrlProvider } from "../useUrlProvider";
import LegalEntitySystemTab from "./legalEntitySystemTab/LegalEntitySystemTab";
import LegalEntityClassificationTab from "./legalEntityClassificationTab/LegalEntityClassificationTab";
import { useLegalEntityBreadCrumbs } from "./useLegalEntityBreadCrumbs.ts";
import { LegalEntityAuditTabs, LegalEntityOverviewPaperTabEnum } from "./LegalEntity.types.ts";
import { getPlanTab } from "../plan/planTab/PlanTab.tsx";
import PageHeaderEditEntityButton, { PageHeaderEditEntityButtonMenuItem } from "../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import RelatedTasksTab from "../taskManagement/relatedTasksTab/RelatedTasksTab.tsx";
import { EntityType } from "../common/Common.types.ts";
import EntityGraphDialog from "@common/components/entityGraph/EntityGraphDialog.tsx";
import { useEntityGraphMenuItem } from "@common/components/entityGraph/EntityGraphMenuItem.hooks.ts";
import { useDeleteLegalEntity } from "./DeleteLegalEntity.hooks.ts";
import DeleteLegalEntityBlockedDialog from "./deleteLegalEntityBlockedDialog/DeleteLegalEntityBlockedDialog.tsx";
import { useAddTaskMenuItem } from "../common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import { useGetRelatedTaskData } from "../taskManagement/relatedTasksTab/RelatedTasksTab.hooks.ts";
import LegalEntityContractTab from "./legalEntityContractTab/legalEntityContractTab.tsx";
import LegalEntityTypeUsageDialog from "./legalEntityTypeUsageDialog/LegalEntityTypeUsageDialog.tsx";
import PlanTabs, { PlanTabContent } from "../plan/planTab/PlanTabs.tsx";
import DotLegalFixedHeightList from "../common/components/dotLegalFixedHeightList/DotLegalFixedHeightList.tsx";
import AuditLogDialog from "../auditlogDialog/AuditLogDialog.tsx";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopBarTile from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarChipsTile from "@common/components/entityDetailTopBar/TopBarChipsTile";
import TopBarNoteTile from "@common/components/entityDetailTopBar/TopBarNoteTile";
import RiskAreaRisksTile from "../risk/riskAreaRisksTile/RiskAreaRisksTile";
import { getOverviewFieldTiles, OverviewFieldTile } from "@common/components/entityDetailTopBar/overviewFieldTiles";
import OverviewTileRows from "@common/components/entityDetailTopBar/OverviewTileRows";
import { LegalEntityStandardField } from "../entityTemplates/EntityTemplate.types";
import DotLegalTabs from "@common/components/dotLegalTabs/DotLegalTabs.tsx";
import { TabContent } from "@common/components/dotLegalTabs/DotLegalTabs.types.ts";
import { dotLegalTabPanelNoPadding } from "@common/components/dotLegalTabPanel/DotLegalTabPanel.tsx";
import LegalEntityStatusChip from "./legalEntities/LegalEntityStatusChip.tsx";
import AllLegalEntityInfo from "./allLegalEntityInfo/AllLegalEntityInfo.tsx";
import LegalEntityStepTab from "./stepTab/LegalEntityStepTab.tsx";

export const topPaperMinHeight = 233;

export interface ILegalEntityProps {
    legalEntityScope: LegalEntityScope;
}

function LegalEntity(props: ILegalEntityProps) {
    let isGroupEntity = props.legalEntityScope === LegalEntityScope.GroupEntity;
    const styles = useLegalEntityStyles();
    const navigate = useNavigate();
    const { translateString } = useTranslation();
    const { getLegalEntityTypeName, getStandardType } = useLegalEntityTypeNames();
    const { id } = useParams<{ id: string }>();
    const { entityGraphMenuItem, isEntityGraphOpen, closeEntityGraph } = useEntityGraphMenuItem();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures, vendorManagementFeatures, addonsFeatures, informationSecurityFeatures, platformFeatures, contractManagementFeatures } =
        usePlanContext();
    const { getLegalEntityEditUrl, getGroupEntitiesUrl, getExternalLegalEntities } = useUrlProvider();
    const addTaskMenuItem = useAddTaskMenuItem();
    const { getLegalEntityTaskApiUrl } = useUrlProvider();

    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();

    const {
        legalEntityLoading,
        legalEntityData,
        sharingsLoading,
        sharingsData,
        tabCounts,
        refecthLegalEntity,
        refetchTabCounts,
        selectedTab,
        setSelectedTab,
        selectedTopTab,
        setSelectedTopTab,
        showChangeCustomerOwnedDialog,
        setShowChangeCustomerOwnedDialog,
        setShowThirdCountryDialog,
        showThirdCountryDialog,
        legalEntityId,
        showLegalEntityTypeUsageDialog,
        setShowLegalEntityTypeUsageDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
    } = useLegalEntityHook(props);
    const [showPreselectedDialog, setShowPreselectedDialog] = useState(false);
    const [showRiskAreasDialog, setShowRiskAreasDialog] = useState(false);
    const { getTaskTabHeader } = useGetRelatedTaskData(id!, getLegalEntityTaskApiUrl(id!));

    const { legalEntityToDelete, setLegalEntityToDelete, deleteLegalEntity, isDeletingLegalEntity, blockedDeletion, closeBlockedDeletion } =
        useDeleteLegalEntity(() => (legalEntityData!.isCustomerOwned ? navigate(getGroupEntitiesUrl()) : navigate(getExternalLegalEntities())));

    const breadCrumbs = useLegalEntityBreadCrumbs(props.legalEntityScope, {
        name: legalEntityData?.name ?? "",
        id: id ?? "",
        userFriendlyId: legalEntityData?.userFriendlyId,
        description: legalEntityData?.description ?? "",
    });

    const getTabLabel = (translationKey: string, count: number | undefined) => {
        return `${translateString(translationKey)}${count ? ` (${count})` : ""}`;
    };

    const getTabContent = () => {
        const content: Array<PlanTabContent> = [];

        const hasDocumentAccess =
            informationSecurityFeatures.legalEntityDocuments || dataProtectionFeatures.dataProcessingAgreements || vendorManagementFeatures.vendorDocuments;
        content.push(
            getPlanTab(
                getTabLabel("documents", hasDocumentAccess ? tabCounts?.documents : undefined),
                getDocumentsTab(),
                LegalEntityAuditTabs.Documents,
                hasDocumentAccess,
                translateString("upgradeDataProtectionPlanInformationSecurityPlanOrVendorManagementPlan")
            )
        );

        if (contractManagementFeatures.hasContractManagement && permissions.contractManagementPermissions.permissions.read) {
            content.push({
                label: getTabLabel("contracts", tabCounts?.contracts),
                content: <LegalEntityContractTab legalEntityId={id!} onContractsChanged={() => refetchTabCounts()} />,
                index: LegalEntityAuditTabs.Contracts,
            });
        }

        if (vendorManagementFeatures.hasVendorManagement) {
            content.push({
                label: getTabLabel("classifications", tabCounts?.classifications),
                content: <LegalEntityClassificationTab legalEntityId={id!} isGroupEntity={isGroupEntity} onClassificationsChanged={() => refetchTabCounts()} />,
                index: LegalEntityAuditTabs.Classifications,
            });
        }

        if (!isGroupEntity && vendorManagementFeatures.hasVendorManagement) {
            content.push({
                label: getTabLabel("audits", tabCounts?.audits),
                content: getAuditsTab(),
                index: LegalEntityAuditTabs.Audits,
            });
        }

        if (permissions.systemPermissions.read) {
            content.push({
                label: getTabLabel("assets", tabCounts?.assets),
                content: <LegalEntitySystemTab legalEntityId={id!} />,
                index: LegalEntityAuditTabs.Assets,
            });
        }

        content.push({
            label: getTabLabel("sharings", sharingsData?.sharingRows.length),
            content: getSharingsTab(),
            index: LegalEntityAuditTabs.Sharings,
        });

        if (vendorManagementFeatures.legalEntityContacts) {
            content.push({
                label: getTabLabel("contacts", tabCounts?.contacts),
                content: getContactsTab(),
                index: LegalEntityAuditTabs.Contacts,
            });
        }

        if (permissions.taskPermissions.read && !addonsFeatures.research) {
            content.push(
                getPlanTab(
                    getTaskTabHeader(),
                    <RelatedTasksTab
                        forEntityId={id!}
                        forEntityType={EntityType.LegalEntity}
                        forEntityName={legalEntityData?.name ?? ""}
                        customApiUrl={getLegalEntityTaskApiUrl(id!)}
                        additionalHeaderTypeTableHeader={translateString("createdFrom")}
                    />,
                    LegalEntityAuditTabs.Tasks,
                    platformFeatures.canAccessRelationsBetweenEntities,
                    translateString("upgradePrivacyPlanToday")
                )
            );
        }

        return content;
    };

    const getSharingsTab = () => {
        return (
            <DotLegalPaper>
                <SharingsBaseTable
                    data={sharingsData}
                    isLoading={sharingsLoading}
                    sessionStorageKey={`legalEntity-${id}-sharings-filters`}
                    emptyStateText={isGroupEntity ? translateString("noSharingsOnGroupEntity") : translateString("noSharingsOnLegalEntity")}
                />
            </DotLegalPaper>
        );
    };

    const getDocumentsTab = () => {
        return (
            <LegalEntityDocumentTab
                onDocumentsChanged={() => refetchTabCounts()}
                isGroupEntity={isGroupEntity}
                legalEntityId={id}
                legalEntityName={legalEntityData?.name}
            />
        );
    };

    const getContactsTab = () => {
        return <LegalEntityContactTab isGroupEntity={isGroupEntity} legalEntityId={id} onContactsChanged={() => refetchTabCounts()} />;
    };

    const getAuditsTab = () => {
        return <LegalEntityAuditTab legalEntityId={id} legalEntityName={legalEntityData?.name} onAuditsChanged={() => refetchTabCounts()} />;
    };

    const getTypesTile = () => {
        return (
            <TopBarTextTile
                label={translateString("type")}
                isLoading={legalEntityLoading}
                neverShowTooltip
                value={legalEntityData?.types.map((type, index) => {
                    const isLast = index === legalEntityData.types.length - 1;
                    if (
                        (getStandardType(type) === StandardLegalEntityType.DataProcessor && legalEntityData.isDataProcessor) ||
                        (getStandardType(type) === StandardLegalEntityType.SubDataProcessor && legalEntityData.isSubDataProcessor)
                    ) {
                        return (
                            <React.Fragment key={type}>
                                <DotLegalLink onClick={() => setShowLegalEntityTypeUsageDialog(type)} linkColor={"primary"}>
                                    {getLegalEntityTypeName(type)}
                                </DotLegalLink>

                                {isLast ? "" : ", "}
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={type}>
                            {getLegalEntityTypeName(type)}
                            {isLast ? "" : ", "}
                        </React.Fragment>
                    );
                })}
            />
        );
    };

    const getResponsibleTile = () => {
        return <TopBarTextTile label={translateString("responsible")} isLoading={legalEntityLoading} value={legalEntityData?.responsible} />;
    };

    const getBusinessAreasTile = () => {
        return <TopBarChipsTile label={translateString("businessAreas")} isLoading={legalEntityLoading} chips={legalEntityData?.businessAreas} />;
    };

    const getNotesTile = () => {
        return (
            <TopBarNoteTile
                onNoteUpdated={() => refecthLegalEntity()}
                postUrl={`/legalentity/${id}/note`}
                note={legalEntityData?.note}
                disabled={!permissions.contractManagementPermissions.permissions.edit}
                entityId={id!}
                entityType={EntityType.LegalEntity}
                isLoading={legalEntityLoading}
            />
        );
    };

    const getPreselectedOnCreateTile = () => {
        return (
            <TopBarTextTile
                label={translateString("preselectedOnCreate")}
                isLoading={legalEntityLoading}
                value={legalEntityData?.isPreselectedOnCreate ? translateString("yes") : translateString("no")}
            />
        );
    };

    // The postal address is four template fields drawn as one tile. The legacy top bar has no template behind
    // it and passes no fields, which keeps every line.
    const getAddressTile = (fields?: Array<LegalEntityStandardField>) => {
        const hasField = (field: LegalEntityStandardField) => fields === undefined || fields.includes(field);

        return (
            <TopBarTextTile
                label={translateString("address")}
                isLoading={legalEntityLoading}
                neverShowTooltip
                value={
                    <React.Fragment>
                        {hasField(LegalEntityStandardField.LegalEntityAddress) && <Box>{legalEntityData?.address}</Box>}
                        {(hasField(LegalEntityStandardField.LegalEntityZipCode) || hasField(LegalEntityStandardField.LegalEntityCity)) && (
                            <Box>{legalEntityData?.zipCodeAndCity}</Box>
                        )}
                        {hasField(LegalEntityStandardField.LegalEntityCountry) && <Box>{legalEntityData?.country}</Box>}
                    </React.Fragment>
                }
            />
        );
    };

    const getCompanyRegistrationNumberTile = () => {
        return (
            <TopBarTextTile
                label={translateString("companyRegistrationNumber")}
                isLoading={legalEntityLoading}
                value={legalEntityData?.companyRegistrationNumber}
            />
        );
    };

    const getWebsiteTile = () => {
        return (
            <TopBarTextTile
                label={translateString("website")}
                isLoading={legalEntityLoading}
                value={
                    legalEntityData?.website ? (
                        <Box
                            component={"a"}
                            href={legalEntityData?.website ?? ""}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: "primary.main",
                                fontWeight: 700,
                                textDecoration: "none",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            {legalEntityData?.website}
                        </Box>
                    ) : undefined
                }
            />
        );
    };

    const getTagsTile = () => {
        return (
            <TopBarTile label={translateString("tags")} isLoading={legalEntityLoading}>
                <DotLegalFixedHeightList
                    isLoading={legalEntityLoading}
                    items={(legalEntityData?.tags ?? []).map((tag) => {
                        return (
                            <React.Fragment key={tag.id}>
                                <DotLegalChip size="small" key={tag.id} color={tag.color} value={tag.name} variant={"outlined"} />
                            </React.Fragment>
                        );
                    })}
                />
            </TopBarTile>
        );
    };

    const getTemplateTile = () => {
        return <TopBarTextTile label={translateString("template")} isLoading={legalEntityLoading} value={legalEntityData?.template ?? undefined} />;
    };

    const getStatusTile = () => {
        return (
            <TopBarTextTile
                label={translateString("status")}
                isLoading={legalEntityLoading}
                neverShowTooltip
                value={<LegalEntityStatusChip isActive={legalEntityData?.isActive ?? true} />}
            />
        );
    };

    const getRiskTile = () => {
        return <RiskAreaRisksTile entityType={EntityType.LegalEntity} entityId={id!} />;
    };

    const hasTagsAccess = platformFeatures.tags && permissions.canAccessTags;

    // Compliance areas, certifications and supervision are left out here on purpose: they sit on the template's
    // compliance step, so the step tab and the all information tab are where they are read now.
    const getOverviewTopRowTiles = () => {
        const fields = legalEntityData?.overviewTopFields ?? [];
        const addressTile: OverviewFieldTile = { key: "address", render: () => getAddressTile(fields) };

        return getOverviewFieldTiles(fields, {
            [LegalEntityStandardField.LegalEntityTypes]: { key: "types", render: () => !isGroupEntity && getTypesTile() },
            [LegalEntityStandardField.LegalEntityStatus]: { key: "status", render: getStatusTile },
            [LegalEntityStandardField.LegalEntityAddress]: addressTile,
            [LegalEntityStandardField.LegalEntityZipCode]: addressTile,
            [LegalEntityStandardField.LegalEntityCity]: addressTile,
            [LegalEntityStandardField.LegalEntityCountry]: addressTile,
            [LegalEntityStandardField.LegalEntityCompanyRegistrationNumber]: {
                key: "companyRegistrationNumber",
                render: getCompanyRegistrationNumberTile,
            },
            [LegalEntityStandardField.LegalEntityWebsite]: { key: "website", render: getWebsiteTile },
        });
    };

    const getOverviewBottomRowTiles = () =>
        getOverviewFieldTiles(legalEntityData?.overviewBottomFields, {
            [LegalEntityStandardField.LegalEntityResponsible]: { key: "responsible", render: getResponsibleTile },
            [LegalEntityStandardField.LegalEntityBusinessAreas]: { key: "businessAreas", render: getBusinessAreasTile },
            [LegalEntityStandardField.LegalEntityTags]: { key: "tags", render: () => hasTagsAccess && getTagsTile() },
        });

    const getPaperWithContent = (content: React.ReactNode) => {
        return (
            <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0, marginTop: -dotLegalTabPanelNoPadding } }}>
                <DotLegalPaper>{content}</DotLegalPaper>
            </Box>
        );
    };

    const getOverviewRows = () => (
        <OverviewTileRows
            tiles={[
                <React.Fragment key="template">{getTemplateTile()}</React.Fragment>,
                ...getOverviewTopRowTiles(),
                ...getOverviewBottomRowTiles(),
                hasEnterpriseRiskManagementAccess && <React.Fragment key="risk">{getRiskTile()}</React.Fragment>,
                isGroupEntity && <React.Fragment key="preselectedOnCreate">{getPreselectedOnCreateTile()}</React.Fragment>,
                // The note is not a template field on the standard template, so it is always shown rather than left to the template.
                <React.Fragment key="note">{getNotesTile()}</React.Fragment>,
            ]}
        />
    );

    const getTopTabsContent = () => {
        const tabs: Array<TabContent> = [
            {
                label: translateString("overview"),
                content: getPaperWithContent(getOverviewRows()),
                index: LegalEntityOverviewPaperTabEnum.Main,
            },
            {
                label: translateString("allLegalEntityData"),
                content: getPaperWithContent(<AllLegalEntityInfo legalEntityId={id!} legalEntityScope={props.legalEntityScope} />),
                index: LegalEntityOverviewPaperTabEnum.AllLegalEntityInfo,
            },
        ];

        (legalEntityData?.templateSteps ?? []).forEach((step) => {
            tabs.push({
                label: step.name,
                content: getPaperWithContent(<LegalEntityStepTab legalEntityId={id!} stepId={step.id} legalEntityScope={props.legalEntityScope} />),
                index: toTopTabIndex(step.order),
            });
        });

        return tabs;
    };

    const getTopBar = () => {
        if (!legalEntityData) {
            return (
                <React.Fragment>
                    <Box sx={{ display: "flex" }}>
                        <Skeleton variant="rounded" width={100} height={42} sx={{ borderRadius: "20px 0 0 0" }} />
                        <Skeleton variant="rounded" width={160} height={42} sx={{ borderRadius: "0 20px 0 0" }} />
                    </Box>
                    <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0 } }}>
                        <DotLegalPaper>
                            <Box sx={{ height: topPaperMinHeight }}>
                                <DotLegalFullScreenSpinner />
                            </Box>
                        </DotLegalPaper>
                    </Box>
                </React.Fragment>
            );
        }

        const tabContent = getTopTabsContent();
        const validatedSelectedTab = tabContent.some((tab) => tab.index === selectedTopTab) ? selectedTopTab : LegalEntityOverviewPaperTabEnum.Main;

        return (
            <DotLegalTabs
                ignoreTextTransformation
                tabStyle="paper-tabs"
                selectedTab={validatedSelectedTab}
                setSelectedTab={setSelectedTopTab}
                content={tabContent}
                fullWidth={false}
                noPadding
            />
        );
    };

    const getEditMenuItems = () => {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [];
        const isNotGroupEntityHasEditAccess = !isGroupEntity && permissions.companyPermissions.edit;

        if ((isGroupEntity && permissions.canManageGroupEntities) || isNotGroupEntityHasEditAccess) {
            menuItems.push({
                menuItem: translateString("editCompany"),
                onClick: () => {
                    navigate(getLegalEntityEditUrl(props.legalEntityScope, id!));
                },
            });
        }

        if (isGroupEntity && permissions.canManageGroupEntities) {
            menuItems.push({
                menuItem: legalEntityData?.isPreselectedOnCreate ? translateString("removeAsPreselectedOnCreate") : translateString("setAsPreselectedOnCreate"),
                onClick: () => {
                    setShowPreselectedDialog(true);
                },
            });
        }

        if ((isGroupEntity && permissions.canManageGroupEntities) || (!isGroupEntity && permissions.companyPermissions.delete)) {
            menuItems.push({
                menuItem: translateString("deleteCompany"),
                onClick: () => {
                    setLegalEntityToDelete({ legalEntityId: id!, name: legalEntityData?.name ?? "" });
                },
            });
        }

        if (permissions.canManageGroupEntities && isNotGroupEntityHasEditAccess) {
            menuItems.push({
                menuItem: translateString("changeToGroupCompany"),
                onClick: () => {
                    setShowChangeCustomerOwnedDialog(true);
                },
                planMenuItemInfo: {
                    hasAccess: addonsFeatures.organizationalManagement,
                    menuName: translateString("changeToGroupCompany"),
                    tooltip: translateString("upgradeToActivateAddOn"),
                },
            });
        }

        if (legalEntityData?.isThirdCountry && ((isGroupEntity && permissions.canManageGroupEntities) || isNotGroupEntityHasEditAccess)) {
            menuItems.push({
                menuItem: translateString("thirdCountrySettings"),
                onClick: () => {
                    setShowThirdCountryDialog(true);
                },
            });
        }

        if (permissions.canAccessEnterpriseRiskManagement) {
            menuItems.push({
                menuItem: translateString("manageRiskAreas"),
                onClick: () => {
                    setShowRiskAreasDialog(true);
                },
            });
        }

        addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
            fromEntityId: id!,
            fromEntityType: EntityType.LegalEntity,
            toEntityType: EntityType.Task,
        });

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(true);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        menuItems.push(entityGraphMenuItem);

        return menuItems;
    };

    return (
        <React.Fragment>
            <Box sx={styles.container}>
                <DotLegalPageHeader breadCrumbs={breadCrumbs} rightComponent={<PageHeaderEditEntityButton menuItems={getEditMenuItems()} />} />

                {getTopBar()}

                <Box sx={styles.tableContainer}>
                    <PlanTabs
                        fullWidth={false}
                        tabStyle="secondary"
                        selectedTab={selectedTab!}
                        setSelectedTab={(val) => setSelectedTab(val)}
                        content={getTabContent()}
                        noPadding
                    />
                </Box>

                {legalEntityToDelete && (
                    <DotLegalDeleteDialogWrapper
                        onOkClick={async () => {
                            deleteLegalEntity(legalEntityToDelete);
                        }}
                        inProgress={isDeletingLegalEntity}
                        onDialogClose={() => setLegalEntityToDelete(undefined)}
                        itemForDeletion={legalEntityToDelete.name}
                        additionalWarning={translateString("deleteLegalEntityAdditionalWarning")}
                        open
                    />
                )}

                {blockedDeletion && (
                    <DeleteLegalEntityBlockedDialog
                        legalEntityName={blockedDeletion.name}
                        blockers={blockedDeletion.blockers}
                        onDialogClose={closeBlockedDeletion}
                    />
                )}

                {showChangeCustomerOwnedDialog && (
                    <ChangeCustomerOwnedDialog
                        isCustomerOwned={legalEntityData!.isCustomerOwned}
                        legalEntityId={id}
                        onCloseDialog={() => setShowChangeCustomerOwnedDialog(false)}
                        companyName={legalEntityData!.name}
                        onUpdate={refecthLegalEntity}
                    />
                )}

                {showThirdCountryDialog && (
                    <LegalEntityThirdCountryDialog
                        thirdCountrySettings={{
                            country: legalEntityData?.country ?? "",
                            countryCode: legalEntityData?.countryCode ?? "",
                            isTIAUnnecessary: legalEntityData?.isTIAUnnecessary ?? false,
                            isTransferBasisUnnecessary: legalEntityData?.isTransferBasisUnnecessary ?? false,
                            levelOfProtection: legalEntityData!.levelOfProtection!,
                            addTransferBasisToSharings: false,
                            transferBasisId: legalEntityData?.transferBasisId,
                        }}
                        legalEntityId={legalEntityId!}
                        onDialogClose={() => setShowThirdCountryDialog(false)}
                        onSaved={() => {
                            refecthLegalEntity();
                            setShowThirdCountryDialog(false);
                        }}
                    />
                )}

                {showRiskAreasDialog && (
                    <EntityRiskAreasDialog entityType={EntityType.LegalEntity} entityId={id!} onClose={() => setShowRiskAreasDialog(false)} />
                )}
            </Box>

            {showLegalEntityTypeUsageDialog && (
                <LegalEntityTypeUsageDialog
                    legalEntityId={legalEntityId!}
                    legalEntityTypeId={showLegalEntityTypeUsageDialog}
                    onDialogClose={() => setShowLegalEntityTypeUsageDialog(null)}
                />
            )}

            {showPreselectedDialog && legalEntityData && (
                <TogglePreselectedOnCreateDialog
                    isPreselectedOnCreate={legalEntityData.isPreselectedOnCreate}
                    legalEntityId={id!}
                    companyName={legalEntityData.name}
                    onCloseDialog={() => setShowPreselectedDialog(false)}
                    onUpdate={refecthLegalEntity}
                />
            )}

            {showAuditLogDialog && (
                <AuditLogDialog
                    entityType={EntityType.LegalEntity}
                    entityId={legalEntityId!}
                    entityName={legalEntityData?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(false)}
                />
            )}

            {isEntityGraphOpen && id && <EntityGraphDialog rootType={EntityType.LegalEntity} rootId={id} onClose={closeEntityGraph} />}
        </React.Fragment>
    );
}

export default LegalEntity;
